/* Which uploaded/shared file formats the in-app viewer can actually render.
 *
 * Every document surface here fetches bytes and hands the browser a blob: URL
 * (see fetchAsBlobUrl) — but only some formats display from one. PDFs go
 * through PdfCanvasViewer and images/text through the viewer modal's iframe;
 * an .xlsx handed to that iframe doesn't display, it downloads. So a "View"
 * button on a spreadsheet described something that never happened. The
 * download is the right behaviour; the label was the problem.
 *
 * Shared by every employee/B2B-facing document list — ClientDetail's received
 * and shared panels, and DocumentBrowser (the Work Documents page and the B2B
 * firm's document tab). The CLIENT portal keeps its own copy as FILE_KINDS in
 * portal/Dashboard.jsx: `portal/` imports nothing from the rest of the app so
 * it can be lifted into a standalone build, the same reason it carries its own
 * brand palette. Keep the two in step.
 *
 * Kept to the server's ALLOWED_DOC_EXTS (routers/clients.py). Anything not
 * listed falls back to "downloads", never to "views" — a View button that
 * silently downloads is the exact thing this exists to prevent.
 */

const VIEWABLE_EXTS = new Set(['pdf', 'png', 'jpg', 'jpeg', 'webp', 'txt']);

// Friendly format names for the formats that DON'T preview, since those are
// the only ones a list has to explain. HEIC previews in Safari and nowhere
// else, so it counts as a download — better to promise less and be right
// everywhere.
const FILE_KIND_LABELS = {
  xls: 'Excel', xlsx: 'Excel',
  doc: 'Word', docx: 'Word',
  csv: 'CSV',
  heic: 'Image',
  pdf: 'PDF', png: 'Image', jpg: 'Image', jpeg: 'Image', webp: 'Image', txt: 'Text',
  mp4: 'Video', m4v: 'Video', mov: 'Video', webm: 'Video',
};

// What each non-previewing format lands in, for "Opens in Excel"-style copy.
const OPENS_IN = {
  xls: 'Excel', xlsx: 'Excel',
  doc: 'Word', docx: 'Word',
  csv: 'your spreadsheet app',
  heic: 'your photo app',
};

// `split('.').pop()` is wrong here: on a filename with no dot it returns the
// whole name, so "Statement" would be labelled "STATEMENT". Index 0 is
// excluded too, so a dotfile (".env") has no extension rather than "env".
export const fileExt = (name) => {
  const s = String(name || '');
  const dot = s.lastIndexOf('.');
  return dot > 0 ? s.slice(dot + 1).toLowerCase() : '';
};

export const canPreviewFile = (name) => VIEWABLE_EXTS.has(fileExt(name));

export const fileKindLabel = (name) => {
  const ext = fileExt(name);
  return FILE_KIND_LABELS[ext] || (ext ? ext.toUpperCase() : 'File');
};

export const fileOpensIn = (name) => OPENS_IN[fileExt(name)] || null;

// Videos are a separate case on purpose, NOT in VIEWABLE_EXTS: they never go
// through a blob: URL (a 2 GB file in memory), they stream from a playback
// link into a <video> element. Only the shared-with-client panel offers them;
// clients cannot upload video, so the other lists never see one. Mirrors the
// server's ALLOWED_VIDEO_EXTS.
const VIDEO_EXTS = new Set(['mp4', 'm4v', 'mov', 'webm']);
export const isVideoFile = (name) => VIDEO_EXTS.has(fileExt(name));
